import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp,
  Users,
  FileText,
  Eye,
  DollarSign,
  Calendar,
  ArrowUp,
  Activity,
  ShieldAlert,
  ShieldCheck,
  Bot,
  Shield,
  UserCheck,
  ArrowRightLeft,
  Smartphone,
  Monitor,
  Tablet,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

type TimeRange = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';

interface AnalyticsOverview {
  totalViews: number;
  totalPages: number;
  avgViewsPerPage: number;
  pagesThisMonth: number;
  totalUniqueVisitors: number;
  topPages: {
    id: string;
    name: string;
    views: number;
    owner: string;
  }[];
}

interface BotStats {
  totalBlocked: number;
  byType: Array<{ name: string; value: number; color: string }>;
  byName: Array<{ name: string; count: number }>;
  dailyBlocked: Array<{ date: string; blocked: number }>;
  topBlockedPages: Array<{ id: string; name: string; count: number }>;
  recentLogs: Array<{
    id: string;
    botName: string;
    botType: string;
    pageName: string;
    ip: string;
    reason: string;
    createdAt: string;
  }>;
}

export const AdminAnalytics: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsOverview | null>(null);
  const [userDistribution, setUserDistribution] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [weeklyStats, setWeeklyStats] = useState<any[]>([]);
  const [botStats, setBotStats] = useState<BotStats>({
    totalBlocked: 0, byType: [], byName: [], dailyBlocked: [], topBlockedPages: [], recentLogs: [],
  });
  const [topPageSort, setTopPageSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'uniqueClicks', dir: 'desc' });
  const [redirectStats, setRedirectStats] = useState<{
    totalRedirects: number;
    byDevice: Array<{ name: string; value: number; color: string }>;
    dailyRedirects: Array<{ date: string; mobile: number; tablet: number; desktop: number; total: number }>;
    topPages: Array<{ id: string; name: string; count: number; mobile: number; tablet: number; desktop: number }>;
    redirectUrls: Array<{ url: string; count: number; device: string }>;
  }>({ totalRedirects: 0, byDevice: [], dailyRedirects: [], topPages: [], redirectUrls: [] });

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  useEffect(() => {
    fetchBotStats();
    fetchRedirectStats();
  }, [timeRange, customStartDate, customEndDate]);

  const fetchAnalyticsData = async () => {
    try {
      if (!token) return;
      
      const [analyticsRes, statsRes, distRes, weeklyRes] = await Promise.all([
        fetch(`${API_URL}/admin/analytics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/dashboard/user-distribution`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/analytics/weekly-stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!analyticsRes.ok || !statsRes.ok || !distRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const analytics = await analyticsRes.json();
      const statsData = await statsRes.json();
      const distribution = await distRes.json();
      const weekly = weeklyRes.ok ? await weeklyRes.json() : [];

      setAnalyticsData(analytics);
      setStats(statsData);
      setUserDistribution(distribution);
      setWeeklyStats(weekly);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
      setLoading(false);
    }
  };

  const fetchBotStats = async () => {
    try {
      if (!token) return;
      if (timeRange === 'custom' && (!customStartDate || !customEndDate)) return;

      let url = `${API_URL}/admin/analytics/bot-stats?timeRange=${timeRange}`;
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setBotStats(data);
      }
    } catch (error) {
      console.error('Error fetching bot stats:', error);
    }
  };

  const fetchRedirectStats = async () => {
    try {
      if (!token) return;
      if (timeRange === 'custom' && (!customStartDate || !customEndDate)) return;

      let url = `${API_URL}/admin/analytics/redirect-stats?timeRange=${timeRange}`;
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setRedirectStats(data);
      }
    } catch (error) {
      console.error('Error fetching redirect stats:', error);
    }
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    if (range === 'custom') {
      setShowCustomPicker(true);
      if (!customStartDate || !customEndDate) {
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        setCustomStartDate(weekAgo.toISOString().split('T')[0]);
        setCustomEndDate(today.toISOString().split('T')[0]);
      }
      setTimeRange('custom');
    } else {
      setShowCustomPicker(false);
      setTimeRange(range);
    }
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case '7d': return 'Last 7 days';
      case '30d': return 'Last 30 days';
      case '90d': return 'Last 90 days';
      case 'custom': return `${customStartDate} - ${customEndDate}`;
      default: return '';
    }
  };

  // Bot logo helper
  const getBotLogo = (name: string): { icon: React.ReactNode; bg: string } => {
    const n = name.toLowerCase();
    if (n.includes('facebook') || n.includes('facebot')) {
      return { bg: '#1877F2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> };
    }
    if (n.includes('google')) {
      return { bg: '#4285F4', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg> };
    }
    if (n.includes('twitter') || n.includes('/x')) {
      return { bg: '#000000', icon: <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> };
    }
    if (n.includes('whatsapp')) {
      return { bg: '#25D366', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> };
    }
    if (n.includes('telegram')) {
      return { bg: '#0088cc', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> };
    }
    if (n.includes('linkedin')) {
      return { bg: '#0A66C2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> };
    }
    if (n.includes('pinterest')) {
      return { bg: '#E60023', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/></svg> };
    }
    if (n.includes('tiktok') || n.includes('bytespider')) {
      return { bg: '#000000', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> };
    }
    if (n.includes('discord')) {
      return { bg: '#5865F2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg> };
    }
    if (n.includes('slack')) {
      return { bg: '#4A154B', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/></svg> };
    }
    if (n.includes('bing')) {
      return { bg: '#008373', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M3.28 0v17.56l5.24 2.94 8.2-4.08v-3.86L8.52 9.18V3.06L3.28 0zm5.24 10.76l8.2 3.38v2.42l-8.2 4.08V10.76z"/></svg> };
    }
    if (n.includes('yahoo')) {
      return { bg: '#6001D2', icon: <span className="text-white font-bold text-xs">Y!</span> };
    }
    // Default bot icon
    return { bg: '#ef4444', icon: <Bot className="w-4 h-4 text-white" /> };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!analyticsData || !stats) {
    return (
      <AdminLayout>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No analytics data available</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-green-600" />
              Analytics Overview
            </h1>
            <p className="text-gray-600 mt-1">Detailed insights and performance metrics</p>
          </div>
          
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: '7d', label: '7 Days' },
              { key: '30d', label: '30 Days' },
              { key: '90d', label: '90 Days' },
              { key: 'custom', label: 'Custom' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTimeRangeChange(key as TimeRange)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  timeRange === key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date Picker */}
        {showCustomPicker && timeRange === 'custom' && (
          <div className="bg-white border border-blue-200 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
            <Calendar className="w-5 h-5 text-blue-500" />
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-green-700 font-medium">Total Revenue</p>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">฿{stats.totalRevenue.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-sm text-green-700">
              <ArrowUp className="w-3 h-3" />
              <span className="text-xs">{stats.revenueGrowth}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-blue-700 font-medium">Total Users</p>
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">{stats.totalUsers.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-sm text-blue-700">
              <ArrowUp className="w-3 h-3" />
              <span className="text-xs">{stats.userGrowth}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-orange-700 font-medium">Total Views</p>
              <Eye className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-orange-900">{analyticsData.totalViews.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-sm text-orange-700">
              <ArrowUp className="w-3 h-3" />
              <span className="text-xs">{stats.viewsGrowth || '+0%'}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-100 border border-indigo-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-indigo-700 font-medium">Unique Visitors</p>
              <UserCheck className="w-5 h-5 text-indigo-600" />
            </div>
            <p className="text-2xl font-bold text-indigo-900">{(analyticsData.totalUniqueVisitors || 0).toLocaleString()}</p>
            <p className="text-xs text-indigo-600 mt-1">
              {analyticsData.totalViews > 0 
                ? `${((analyticsData.totalUniqueVisitors || 0) / analyticsData.totalViews * 100).toFixed(1)}% of views` 
                : '0% of views'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-purple-700 font-medium">Total Pages</p>
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">{analyticsData.totalPages.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-sm text-purple-700">
              <ArrowUp className="w-3 h-3" />
              <span className="text-xs">{stats.pageGrowth}</span>
            </div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-50 p-2 rounded-lg">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Avg Views per Page</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analyticsData.avgViewsPerPage.toLocaleString()}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-50 p-2 rounded-lg">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Pages This Month</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analyticsData.pagesThisMonth}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-50 p-2 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Pro Subscribers</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.proUsers}</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Growth Trend Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Growth Trends</h3>
            {weeklyStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={weeklyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} name="Users" />
                  <Line type="monotone" dataKey="pages" stroke="#8b5cf6" strokeWidth={2} name="Pages" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                <p>No weekly data available</p>
              </div>
            )}
          </div>

          {/* User Distribution */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">User Distribution</h3>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {userDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Pages Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Top Performing Pages
            </h3>
            <p className="text-sm text-gray-500 mt-1">Sorted by {topPageSort.key === 'uniqueClicks' ? 'Unique Clicks' : topPageSort.key === 'views' ? 'Views' : topPageSort.key === 'uniqueVisitors' ? 'Unique Visitors' : 'Conv. Rate'} · Click column header to sort</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Page Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Owner</th>
                  {[
                    { key: 'views', label: 'Views' },
                    { key: 'uniqueVisitors', label: 'Unique' },
                    { key: 'uniqueClicks', label: 'Clicks' },
                    { key: 'conversionRate', label: 'Conv. Rate' },
                  ].map(col => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      style={{ color: topPageSort.key === col.key ? '#2563eb' : '#374151' }}
                      onClick={() => setTopPageSort(prev => ({
                        key: col.key,
                        dir: prev.key === col.key ? (prev.dir === 'desc' ? 'asc' : 'desc') : 'desc',
                      }))}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        {col.label}
                        {topPageSort.key === col.key && (
                          <span className="text-blue-600">{topPageSort.dir === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Performance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...analyticsData.topPages]
                  .sort((a: any, b: any) => {
                    const aVal = topPageSort.key === 'conversionRate' ? parseFloat(a.conversionRate || '0') : (a[topPageSort.key] || 0);
                    const bVal = topPageSort.key === 'conversionRate' ? parseFloat(b.conversionRate || '0') : (b[topPageSort.key] || 0);
                    return topPageSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
                  })
                  .slice(0, 10)
                  .map((page: any, index: number) => {
                    const maxVal = Math.max(...analyticsData.topPages.map((p: any) => p[topPageSort.key === 'conversionRate' ? 'uniqueClicks' : topPageSort.key] || 0), 1);
                    const curVal = topPageSort.key === 'conversionRate' ? (page.uniqueClicks || 0) : (page[topPageSort.key] || 0);
                    return (
                      <tr key={page.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-gray-100 text-gray-600' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{page.name}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{page.owner}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-semibold text-gray-900">{(page.views || 0).toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-indigo-600">{(page.uniqueVisitors || 0).toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-purple-600">{(page.uniqueClicks || 0).toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className={`text-sm font-bold ${
                            parseFloat(page.conversionRate || '0') >= 5 ? 'text-green-600' :
                            parseFloat(page.conversionRate || '0') >= 2 ? 'text-yellow-600' : 'text-gray-500'
                          }`}>{page.conversionRate || '0.0'}%</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="w-full max-w-[100px] bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min((curVal / maxVal) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {analyticsData.topPages.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No page data available</p>
            </div>
          )}
        </div>

        {/* Views Trend Chart */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Views Trend</h3>
          {weeklyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Legend />
                <Bar dataKey="views" fill="#3b82f6" name="Page Views" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              <p>No views data available</p>
            </div>
          )}
        </div>

        {/* =============================================
            Device Redirect Analytics (Admin - All Pages)
            ============================================= */}
        {redirectStats.totalRedirects > 0 && (
          <div className="mt-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
                <ArrowRightLeft className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Device Redirect (All Pages)</h2>
                <p className="text-sm text-gray-500">Visitors redirected based on device across the platform - {getTimeRangeLabel()}</p>
              </div>
            </div>

            {/* Redirect Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-700">Total Redirects</p>
                  <ArrowRightLeft className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{redirectStats.totalRedirects.toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-1">{getTimeRangeLabel()}</p>
              </div>

              {[
                { key: 'Mobile', icon: <Smartphone className="w-5 h-5 text-blue-500" /> },
                { key: 'Tablet', icon: <Tablet className="w-5 h-5 text-emerald-500" /> },
                { key: 'Desktop', icon: <Monitor className="w-5 h-5 text-purple-500" /> },
              ].map(({ key, icon }) => {
                const count = redirectStats.byDevice.find(d => d.name === key)?.value || 0;
                const pct = redirectStats.totalRedirects > 0 
                  ? ((count / redirectStats.totalRedirects) * 100).toFixed(1) 
                  : '0.0';
                return (
                  <div key={key} className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">{key}</p>
                      {icon}
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{count.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{pct}% of redirects</p>
                  </div>
                );
              })}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Redirect by Device Pie */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Redirects by Device</h3>
                {redirectStats.byDevice.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={redirectStats.byDevice} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                          {redirectStats.byDevice.map((entry, index) => (
                            <Cell key={`redirect-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {redirectStats.byDevice.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-sm text-gray-600">{item.name}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900">{item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-gray-400">No redirect data</div>
                )}
              </div>

              {/* Top Redirected Pages */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Top Redirected Pages</h3>
                {redirectStats.topPages.length > 0 ? (
                  <div className="space-y-3">
                    {redirectStats.topPages.map((page, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full font-bold text-xs">
                            {index + 1}
                          </div>
                          <div>
                            <span className="font-medium text-gray-900 text-sm">{page.name}</span>
                            <div className="flex gap-2 mt-0.5">
                              {page.mobile > 0 && <span className="text-xs text-blue-600">📱 {page.mobile}</span>}
                              {page.tablet > 0 && <span className="text-xs text-emerald-600">📟 {page.tablet}</span>}
                              {page.desktop > 0 && <span className="text-xs text-purple-600">🖥️ {page.desktop}</span>}
                            </div>
                          </div>
                        </div>
                        <span className="font-bold text-blue-600 text-sm">{page.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-gray-400">No data</div>
                )}
              </div>
            </div>

            {/* Daily Redirect Trend */}
            {redirectStats.dailyRedirects.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Daily Redirects by Device</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={redirectStats.dailyRedirects}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="mobile" stackId="a" fill="#3b82f6" name="Mobile" />
                    <Bar dataKey="tablet" stackId="a" fill="#10b981" name="Tablet" />
                    <Bar dataKey="desktop" stackId="a" fill="#8b5cf6" name="Desktop" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* =============================================
            Bot Cloaking Analytics (Admin - All Pages)
            ============================================= */}
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Bot Cloaking (All Pages)</h2>
              <p className="text-sm text-gray-500">Bot traffic blocked across the entire platform - {getTimeRangeLabel()}</p>
            </div>
          </div>

          {/* Bot Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-red-700">Bots Blocked</p>
                <ShieldAlert className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{botStats.totalBlocked.toLocaleString()}</p>
              <p className="text-xs text-red-600 mt-2">{getTimeRangeLabel()}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-700">Bot Types</p>
                <Bot className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{botStats.byType.length}</p>
              <p className="text-xs text-blue-600 mt-2">categories detected</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-green-700">Pages Protected</p>
                <Shield className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{botStats.topBlockedPages?.length || 0}</p>
              <p className="text-xs text-green-600 mt-2">pages with cloaking active</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Blocked by Channel */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Blocked by Channel</h3>
              {botStats.byType.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={botStats.byType} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                        {botStats.byType.map((entry, index) => (
                          <Cell key={`bot-cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {botStats.byType.map((item, index) => {
                      const channelLogo = getBotLogo(item.name);
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-5 h-5 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: channelLogo.bg }}>
                              {channelLogo.icon}
                            </div>
                            <span className="text-sm text-gray-600">{item.name}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900">{item.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                  <ShieldCheck className="w-12 h-12 mb-3 text-gray-300" />
                  <p>No bots blocked yet</p>
                </div>
              )}
            </div>

            {/* Top Blocked Bots */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Top Blocked Bots</h3>
              {botStats.byName.length > 0 ? (
                <div className="space-y-3">
                  {botStats.byName.map((item, index) => {
                    const maxCount = botStats.byName[0]?.count || 1;
                    const percentage = (item.count / maxCount) * 100;
                    const botLogo = getBotLogo(item.name);
                    return (
                      <div key={index} className="relative">
                        <div className="flex items-center justify-between relative z-10 p-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: botLogo.bg }}>
                              {botLogo.icon}
                            </div>
                            <span className="font-medium text-gray-900 text-sm">{item.name}</span>
                          </div>
                          <span className="font-bold text-gray-900 text-sm">{item.count.toLocaleString()}</span>
                        </div>
                        <div className="absolute inset-0 bg-red-50 rounded-lg" style={{ width: `${percentage}%` }} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                  <Bot className="w-12 h-12 mb-3 text-gray-300" />
                  <p>No bot data available</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Top Blocked Pages */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Top Protected Pages</h3>
              {botStats.topBlockedPages && botStats.topBlockedPages.length > 0 ? (
                <div className="space-y-3">
                  {botStats.topBlockedPages.map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full font-bold text-xs">
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900 text-sm">{page.name}</span>
                      </div>
                      <span className="font-bold text-red-600 text-sm">{page.count.toLocaleString()} blocked</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-400">No data</div>
              )}
            </div>

            {/* Recent Bot Logs */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Recent Bot Activity</h3>
              {botStats.recentLogs && botStats.recentLogs.length > 0 ? (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {botStats.recentLogs.map((log, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="bg-red-100 p-1.5 rounded-lg mt-0.5">
                        <ShieldAlert className="w-3.5 h-3.5 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{log.botName}</p>
                        <p className="text-xs text-gray-500 truncate">{log.pageName}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(log.createdAt).toLocaleString('th-TH')}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium whitespace-nowrap">
                        {log.botType === 'social_media' ? 'Social'
                          : log.botType === 'search_engine' ? 'Search'
                          : log.botType === 'seo_tool' ? 'SEO'
                          : log.botType === 'spy_tool' ? 'Spy'
                          : 'Bot'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-400">No recent logs</div>
              )}
            </div>
          </div>

          {/* Daily Blocked Trend */}
          {botStats.dailyBlocked.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Daily Bots Blocked</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={botStats.dailyBlocked}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="blocked" fill="#ef4444" name="Bots Blocked" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
